'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { runWhenIdle } from '@/src/lib/utils/runWhenIdle';

// The idle deadline also caps how long a keyboard-only user waits for a focusable map.
export const MAP_ACTIVATION_IDLE_TIMEOUT_MS = 2500;
// Deadline cap for an uncovered panel. The idle callback normally fires well
// before it; a panel with nothing drawn over it just can't wait out the longer one.
const MAP_ACTIVATION_UNCOVERED_TIMEOUT_MS = 150;
// Matches the overlay's transitionDuration in PersistentSearchShell.
const MAP_PREVIEW_FADE_OUT_MS = 400;
// Caps how long the opaque overlay can swallow pointer events when Maps JS is
// blocked and tilesloaded never fires.
const MAP_PREVIEW_FAILSAFE_DISMISS_MS = 4000;
// The Maps JS loader's origin, warmed from here rather than with a hint in the
// document head: the head budget in `firstPartyHosts` is spent on the LCP image
// hosts, and a mobile page load never fetches Maps JS at all (`shouldRenderMap`
// is desktop-or-opened-map), so a static hint would be waste on most traffic.
const GOOGLE_MAPS_LOADER_ORIGIN = 'https://maps.googleapis.com';

type DeferredMapIslandOptions = {
  hasPreview: boolean;
  shouldRenderMap: boolean;
  mobileMapActive: boolean;
  pathname: string;
};

/**
 * Open the loader's socket before the island asks for it. Measured on a desktop
 * SRP with CDP timing (resource timing masks these phases cross-origin): the
 * loader pays 34-47ms of connect, 21-34ms of that TLS, on every cold load. The
 * tile host needs no hint of its own — Maps JS warms mapsresources-pa itself
 * during boot, and its tile requests already report a reused connection.
 *
 * No `crossorigin`: the loader is a plain `<script src>`, so a CORS-anonymous
 * hint would warm a socket that request cannot use.
 */
function warmGoogleMapsLoaderOrigin(): void {
  if (document.querySelector(`link[rel="preconnect"][href="${GOOGLE_MAPS_LOADER_ORIGIN}"]`) !== null) return;

  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = GOOGLE_MAPS_LOADER_ORIGIN;
  document.head.appendChild(link);
}

/**
 * Defers mounting the Google Maps island while a static map preview covers the
 * panel: the island mounts on first panel interaction or post-hydration idle,
 * and the preview fades once the map's tiles have painted.
 */
export function useDeferredMapIsland({
  hasPreview,
  shouldRenderMap,
  mobileMapActive,
  pathname,
}: DeferredMapIslandOptions) {
  const [mapActivated, setMapActivated] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);
  const [isPreviewLoaded, setIsPreviewLoaded] = useState(false);
  const [isPreviewDismissed, setIsPreviewDismissed] = useState(false);
  const initialPathnameRef = useRef(pathname);

  const activateMap = useCallback(() => setMapActivated(true), []);
  const handleMapReady = useCallback(() => setIsMapReady(true), []);
  const handlePreviewLoad = useCallback(() => setIsPreviewLoaded(true), []);
  // A failed preview image leaves nothing worth covering the map with.
  const handlePreviewError = useCallback(() => {
    setIsPreviewDismissed(true);
    setMapActivated(true);
  }, []);

  const showPreview = hasPreview && !isPreviewDismissed;
  // Mobile map opens are gated too: constructing Maps JS in the tap's own commit
  // cost ~190ms of INP (HP-8727).
  const isGateActive = !mapActivated && (showPreview || mobileMapActive);
  const showIsland = shouldRenderMap && !isGateActive;
  // The preview is lg-only, so it never covers a panel opened on mobile.
  const isPanelCovered = showPreview && !mobileMapActive;
  const canIdleActivate = !isPanelCovered || isPreviewLoaded;

  // Warm the loader socket as soon as this viewport is known to want a map. On
  // desktop that is hydration, which runs ~400ms before the idle callback below
  // actually requests Maps JS; on mobile it waits for the map to be opened.
  useEffect(() => {
    if (!shouldRenderMap) return;
    warmGoogleMapsLoaderOrigin();
  }, [shouldRenderMap]);

  // A soft navigation makes the snapshotted preview stale.
  useEffect(() => {
    if (pathname !== initialPathnameRef.current) setIsPreviewDismissed(true);
  }, [pathname]);

  useEffect(() => {
    if (!shouldRenderMap || !isGateActive || !canIdleActivate) return;
    return runWhenIdle(activateMap, {
      timeoutMs: isPanelCovered ? MAP_ACTIVATION_IDLE_TIMEOUT_MS : MAP_ACTIVATION_UNCOVERED_TIMEOUT_MS,
    });
  }, [activateMap, canIdleActivate, isGateActive, isPanelCovered, shouldRenderMap]);

  // The island unmounts with the mobile panel, so re-arm for the next open;
  // desktop holds `shouldRenderMap` true and never re-arms.
  useEffect(() => {
    if (!shouldRenderMap) setMapActivated(false);
  }, [shouldRenderMap]);

  // Ready tiles unmount the overlay once its fade completes; an island whose
  // tiles never paint gets the failsafe instead.
  useEffect(() => {
    if (!showPreview || (!isMapReady && !showIsland)) return;
    const handle = window.setTimeout(
      () => setIsPreviewDismissed(true),
      isMapReady ? MAP_PREVIEW_FADE_OUT_MS : MAP_PREVIEW_FAILSAFE_DISMISS_MS,
    );
    return () => window.clearTimeout(handle);
  }, [isMapReady, showIsland, showPreview]);

  return {
    showIsland,
    showPreview,
    isMapReady,
    onActivate: activateMap,
    onMapReady: handleMapReady,
    onPreviewLoad: handlePreviewLoad,
    onPreviewError: handlePreviewError,
  };
}
